import { useState, useEffect, useRef } from 'react';
import {
  Container, Card, CardBody, CardHeader, CardFooter,
  Input, FormGroup, Label,
} from 'reactstrap';
import { Save, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Settings, MessageSquare, Plus, Edit2, Trash2, X, Info } from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro, alertConfirmar } from '../../utils/alerts';

const MAX_CONTEXT = 5000;

const iconBox = (Icon) => (
  <div style={{
    background: '#f0eeff', borderRadius: 10,
    padding: '7px 8px', display: 'inline-flex', flexShrink: 0,
  }}>
    <Icon size={18} color="#7366ff" />
  </div>
);

const cardHeader = (Icon, title, subtitle) => (
  <CardHeader style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {iconBox(Icon)}
      <div>
        <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{title}</h6>
        <small style={{ color: '#94a3b8', fontSize: 12 }}>{subtitle}</small>
      </div>
    </div>
  </CardHeader>
);

export default function Configuracoes() {
  const contextRef = useRef();

  const [context, setContext]     = useState('');
  const [savingCtx, setSavingCtx] = useState(false);
  const [ctxSaved, setCtxSaved]   = useState(false);

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmSenha, setConfirm] = useState('');
  const [showNova, setShowNova]   = useState(false);
  const [showConf, setShowConf]   = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const [iscas, setIscas]           = useState([]);
  const [loadingIscas, setLoading]  = useState(false);
  const [modal, setModal]           = useState(null); // null | { mode: 'add' | 'edit', item?: {} }
  const [iscaText, setIscaText]     = useState('');
  const [savingIsca, setSavingIsca] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const senhasOk   = novaSenha.length > 0 && novaSenha === confirmSenha;
  const senhasDiff = novaSenha.length > 0 && confirmSenha.length > 0 && novaSenha !== confirmSenha;
  const canSavePwd = senhasOk && !savingPwd;

  useEffect(() => {
    api.get('/configuracoes')
      .then(r => { if (r.data?.company_context) setContext(r.data.company_context); })
      .catch(() => {});
    setTimeout(() => contextRef.current?.focus(), 100);
    loadIscas();
  }, []);

  async function loadIscas() {
    setLoading(true);
    try {
      const r = await api.get('/configuracoes/iscas');
      setIscas(r.data || []);
    } catch (e) {
      alertErro('Erro ao carregar iscas', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function saveContext() {
    if (savingCtx) return;
    setSavingCtx(true);
    setCtxSaved(false);
    try {
      await api.put('/configuracoes', { company_context: context });
      setCtxSaved(true);
      setTimeout(() => setCtxSaved(false), 3000);
    } catch (e) {
      alertErro('Erro ao salvar', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setSavingCtx(false);
    }
  }

  async function saveSenha() {
    if (!canSavePwd) return;
    setSavingPwd(true);
    try {
      await api.put('/configuracoes/senha', { password: novaSenha });
      setNovaSenha('');
      setConfirm('');
      const div = document.createElement('div');
      div.textContent = 'Senha atualizada com sucesso!';
      Object.assign(div.style, {
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        background: '#54ba4a', color: '#fff', padding: '10px 20px',
        borderRadius: 8, fontWeight: 600, fontSize: 13,
        boxShadow: '0 4px 12px rgba(0,0,0,.15)',
      });
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 3000);
    } catch (e) {
      alertErro('Erro ao atualizar senha', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setSavingPwd(false);
    }
  }

  function openAdd() {
    setIscaText('');
    setModal({ mode: 'add' });
  }

  function openEdit(item) {
    setIscaText(item.content);
    setModal({ mode: 'edit', item });
  }

  function closeModal() {
    if (savingIsca) return;
    setModal(null);
    setIscaText('');
  }

  async function saveIsca() {
    if (!iscaText.trim() || savingIsca) return;
    setSavingIsca(true);
    try {
      if (modal.mode === 'add') {
        const r = await api.post('/configuracoes/iscas', { content: iscaText });
        setIscas(prev => [...prev, r.data]);
      } else {
        const r = await api.put(`/configuracoes/iscas/${modal.item.id}`, { content: iscaText });
        setIscas(prev => prev.map(i => i.id === modal.item.id ? r.data : i));
      }
      closeModal();
    } catch (e) {
      alertErro('Erro ao salvar frase', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setSavingIsca(false);
    }
  }

  async function deleteIsca(id) {
    if (deletingId) return;
    const confirmed = await alertConfirmar('Remover isca?', 'Tem certeza que deseja remover esta frase isca?');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await api.delete(`/configuracoes/iscas/${id}`);
      setIscas(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      alertErro('Erro ao remover', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  }

  const primaryBtn = (active) => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600,
    fontSize: 13, cursor: active ? 'pointer' : 'not-allowed',
    background: active ? '#7366ff' : '#c4b5fd',
    color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'background .15s',
  });

  const eyeBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#6c757d', padding: '0 8px', display: 'flex',
  };

  const pwdField = (value, onChange, placeholder, show, toggleShow, border) => (
    <div style={{
      display: 'flex', alignItems: 'center',
      border: `1px solid ${border}`, borderRadius: 6, overflow: 'hidden',
    }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ flex: 1, border: 'none', outline: 'none', padding: '7px 12px', fontSize: 13 }}
      />
      <button style={eyeBtn} onClick={toggleShow} type="button" tabIndex={-1}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );

  return (
    <>
      <Breadcrumbs mainTitle="Configurações" parent="Topo Prospect" title="Configurações" />
      <Container fluid>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>

          {/* Card 1 — Contexto da empresa */}
          <Card className="mb-3">
            {cardHeader(Settings, 'Contexto da empresa', 'Como o agente de IA vai se apresentar')}
            <CardBody style={{ padding: '20px' }}>
              <p style={{ fontSize: 12, color: '#6c757d', marginBottom: 10, lineHeight: 1.5 }}>
                Descreva sua empresa com o máximo de detalhes — segmento, produtos, diferenciais,
                público-alvo, tom de voz. O agente de IA usará esse contexto para se comunicar
                melhor com seus leads.
              </p>
              <div style={{ position: 'relative' }}>
                <Input
                  innerRef={contextRef}
                  type="textarea"
                  value={context}
                  onChange={e => e.target.value.length <= MAX_CONTEXT && setContext(e.target.value)}
                  placeholder="Ex: Somos a Chatfire, empresa de automação de WhatsApp para pequenas e médias empresas..."
                  style={{
                    minHeight: 140, resize: 'vertical', fontSize: 13,
                    paddingBottom: 28,
                    color: context ? undefined : '#bbb',
                  }}
                />
                <span style={{
                  position: 'absolute', bottom: 8, right: 12,
                  fontSize: 11,
                  color: context.length > MAX_CONTEXT * 0.9 ? '#f44336' : '#94a3b8',
                }}>
                  {context.length}/{MAX_CONTEXT}
                </span>
              </div>
            </CardBody>
            <CardFooter style={{ padding: '12px 20px', background: '#fafafa', borderTop: '1px solid #f1f1f1', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={saveContext} disabled={savingCtx} style={primaryBtn(!savingCtx)}>
                {ctxSaved
                  ? <><CheckCircle size={13} /> Salvo!</>
                  : <><Save size={13} /> {savingCtx ? 'Salvando...' : 'Salvar contexto'}</>
                }
              </button>
            </CardFooter>
          </Card>

          {/* Card 2 — Trocar senha */}
          <Card className="mb-3">
            {cardHeader(Lock, 'Trocar senha', 'Atualize sua senha de acesso')}
            <CardBody style={{ padding: '20px' }}>
              <div style={{ display: 'flex', gap: 16 }}>
                <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                  <Label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                    Nova senha
                  </Label>
                  {pwdField(
                    novaSenha,
                    e => setNovaSenha(e.target.value),
                    'Nova senha',
                    showNova,
                    () => setShowNova(v => !v),
                    '#dee2e6',
                  )}
                </FormGroup>

                <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                  <Label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                    Confirmar senha
                  </Label>
                  {pwdField(
                    confirmSenha,
                    e => setConfirm(e.target.value),
                    'Confirmar senha',
                    showConf,
                    () => setShowConf(v => !v),
                    senhasDiff ? '#f44336' : senhasOk ? '#54ba4a' : '#dee2e6',
                  )}
                  {senhasOk && (
                    <small style={{ color: '#54ba4a', fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CheckCircle size={11} /> As senhas coincidem
                    </small>
                  )}
                  {senhasDiff && (
                    <small style={{ color: '#f44336', fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <AlertCircle size={11} /> As senhas não coincidem
                    </small>
                  )}
                </FormGroup>
              </div>
            </CardBody>
            <CardFooter style={{ padding: '12px 20px', background: '#fafafa', borderTop: '1px solid #f1f1f1', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={saveSenha} disabled={!canSavePwd} style={primaryBtn(canSavePwd)}>
                <Lock size={13} /> {savingPwd ? 'Atualizando...' : 'Atualizar senha'}
              </button>
            </CardFooter>
          </Card>

          {/* Card 3 — Mensagens Isca */}
          <Card className="mb-3">
            {cardHeader(MessageSquare, 'Mensagens Isca', `${iscas.length} frase${iscas.length !== 1 ? 's' : ''} cadastrada${iscas.length !== 1 ? 's' : ''}`)}
            <CardBody style={{ padding: '20px' }}>
              {loadingIscas ? (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Carregando...</p>
              ) : iscas.length === 0 ? (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0, fontStyle: 'italic' }}>
                  Nenhuma frase cadastrada ainda.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {iscas.map(isca => (
                    <div key={isca.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: '#f8f9fa', borderRadius: 8, padding: '10px 14px',
                      border: '1px solid #e9ecef',
                    }}>
                      <span style={{ flex: 1, fontSize: 13, color: '#344050' }}>{isca.content}</span>
                      <button
                        onClick={() => openEdit(isca)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7366ff', padding: '2px 6px', borderRadius: 4, display: 'flex' }}
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => deleteIsca(isca.id)}
                        disabled={deletingId === isca.id}
                        style={{ background: 'none', border: 'none', cursor: deletingId === isca.id ? 'not-allowed' : 'pointer', color: '#f44336', padding: '2px 6px', borderRadius: 4, display: 'flex' }}
                        title="Remover"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={openAdd} style={{
                ...primaryBtn(true),
                padding: '7px 16px', fontSize: 12,
              }}>
                <Plus size={13} /> Adicionar frase
              </button>

              <div style={{
                marginTop: 16, padding: '10px 14px',
                background: '#f0eeff', borderRadius: 8,
                display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <Info size={14} color="#7366ff" style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ margin: 0, fontSize: 12, color: '#5b4fcf', lineHeight: 1.5 }}>
                  O engine escolhe uma frase aleatória a cada disparo. Cadastre variações para parecer mais natural e evitar bloqueios.
                </p>
              </div>
            </CardBody>
          </Card>

        </div>
      </Container>

      {/* Modal Mensagem Isca */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                {modal.mode === 'add' ? 'Adicionar frase isca' : 'Editar frase isca'}
              </h6>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
              Use <code style={{ background: '#f0eeff', padding: '1px 5px', borderRadius: 3, color: '#7366ff' }}>{'{nome}'}</code> para personalizar com o nome do lead.
            </p>

            <Input
              type="textarea"
              value={iscaText}
              onChange={e => setIscaText(e.target.value)}
              placeholder='Ex: Oi {nome}, tudo bem?'
              style={{ minHeight: 90, fontSize: 13, resize: 'vertical', marginBottom: 16 }}
              autoFocus
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={closeModal} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #dee2e6',
                background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6c757d',
              }}>
                Cancelar
              </button>
              <button
                onClick={saveIsca}
                disabled={!iscaText.trim() || savingIsca}
                style={primaryBtn(!!iscaText.trim() && !savingIsca)}
              >
                <Save size={13} /> {savingIsca ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
